/**
 * Shared template every Guide topic wears, so the docs surface stays uniform:
 * a hero card (eyebrow → sentence-case display title → intro → optional CTA,
 * with an optional boxed "What's live today" / "Next on the roadmap" panel
 * riding the right column), an optional numbered walkthrough card, then a free
 * children slot for topic-specific sections. Ported from the sibling
 * jnj-adaptive-analysis-layer guide, retuned to this repo's brand tokens.
 *
 * Brand: J&J red is reserved for the single CTA + the step-number badges; the
 * decorative hero tint sits at 6% opacity so it reads as warmth, not a second
 * hero color. Green checks / faint dots mirror the rest of the app's idiom.
 */

import { ArrowRight, CheckCircle2, CircleDot, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface GuideBulletItem {
  text: ReactNode;
  tone?: "default" | "muted";
}

export interface GuideCta {
  to: string;
  label: string;
}

export interface GuideWalkthroughStep {
  step: string;
  body: ReactNode;
}

export interface GuidePageProps {
  /** Uppercase-tracked kicker above the title. */
  eyebrow: string;
  /** The topic's heading — sentence case, rendered in the display face. */
  title: ReactNode;
  /** One or two sentences of context under the title. */
  intro: ReactNode;
  /** Optional single primary action for the topic. */
  cta?: GuideCta;
  /** Heading for the live-bullet block (defaults to "What's live today"). */
  liveHeading?: string;
  /** Heading for the roadmap-bullet block (defaults to "Next on the roadmap"). */
  futureHeading?: string;
  /** Green-check bullets — what is wired up today. */
  liveBullets?: GuideBulletItem[];
  /** Faint bullets — what is planned but not built. */
  futureBullets?: GuideBulletItem[];
  /** Optional numbered walkthrough steps. */
  walkthrough?: GuideWalkthroughStep[];
  /** Topic-specific sections rendered below the hero + walkthrough. */
  children?: ReactNode;
}

export function GuidePage({
  eyebrow,
  title,
  intro,
  cta,
  liveHeading = "What's live today",
  futureHeading = "Next on the roadmap",
  liveBullets,
  futureBullets,
  walkthrough,
  children,
}: GuidePageProps) {
  const hasPanel =
    (liveBullets && liveBullets.length > 0) || (futureBullets && futureBullets.length > 0);

  return (
    <div className="space-y-8">
      <section className="border-border bg-card relative overflow-hidden rounded-lg border shadow-sm">
        <div
          aria-hidden
          className="bg-primary pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full opacity-[0.06] blur-3xl"
        />
        <div
          className={cn(
            "relative grid grid-cols-1 gap-6 p-6 sm:p-8",
            hasPanel && "lg:grid-cols-[1.5fr_1fr] lg:gap-10",
          )}
        >
          <div className="min-w-0">
            <span className="text-muted text-xs font-medium tracking-[0.18em] uppercase">
              {eyebrow}
            </span>
            <h1 className="font-heading text-foreground mt-3 text-3xl leading-tight font-normal tracking-tight sm:text-4xl">
              {title}
            </h1>
            <p className="text-muted mt-4 max-w-2xl text-sm leading-relaxed sm:text-base">
              {intro}
            </p>
            {cta && (
              <div className="mt-6">
                <Button asChild size="lg">
                  <Link to={cta.to}>
                    {cta.label}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </div>
            )}
          </div>

          {hasPanel && (
            <div className="border-border bg-background flex flex-col gap-3 rounded-md border p-5">
              {liveBullets && liveBullets.length > 0 && (
                <>
                  <div className="flex items-center gap-2">
                    <Sparkles className="text-muted h-4 w-4" aria-hidden />
                    <p className="text-foreground text-sm font-semibold">{liveHeading}</p>
                  </div>
                  <GuideBulletList items={liveBullets} />
                </>
              )}
              {futureBullets && futureBullets.length > 0 && (
                <>
                  <div className="mt-2 flex items-center gap-2">
                    <CircleDot className="text-warm-gray-06 h-4 w-4" aria-hidden />
                    <p className="text-muted text-sm font-semibold">{futureHeading}</p>
                  </div>
                  <GuideBulletList
                    items={futureBullets.map((b) => ({ ...b, tone: "muted" as const }))}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {walkthrough && walkthrough.length > 0 && (
        <section className="border-border bg-card rounded-lg border shadow-sm">
          <header className="border-border border-b px-5 py-4">
            <h2 className="text-foreground text-sm font-semibold">Walkthrough</h2>
          </header>
          <ol className="divide-border divide-y">
            {walkthrough.map((s, i) => (
              <li key={i} className="grid grid-cols-[auto_minmax(0,1fr)] gap-4 px-5 py-4">
                <span className="bg-primary text-primary-foreground inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-foreground text-sm font-medium">{s.step}</p>
                  <p className="text-muted mt-1 text-sm leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {children}
    </div>
  );
}

/** Shared check/dot bullet list — green checks for live items, faint dots for
 * roadmap items — matching the rest of the app's idiom so the two read as one. */
export function GuideBulletList({
  items,
  className,
}: {
  items: GuideBulletItem[];
  className?: string;
}) {
  return (
    <ul className={cn("space-y-2 text-sm leading-6", className)}>
      {items.map((item, i) => (
        <GuideBullet key={i} tone={item.tone}>
          {item.text}
        </GuideBullet>
      ))}
    </ul>
  );
}

function GuideBullet({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "muted";
}) {
  const Icon = tone === "muted" ? CircleDot : CheckCircle2;
  return (
    <li className="flex items-start gap-2">
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "muted" ? "text-warm-gray-06" : "text-jj-green-03",
        )}
        aria-hidden
      />
      <span className={cn(tone === "muted" ? "text-muted-foreground" : "text-foreground")}>
        {children}
      </span>
    </li>
  );
}
